import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { strings } from "../../services/Localization";
import PageContent from "../PageContent";
import { Modal, Button } from 'react-bootstrap';
import { ActionType, BulkActionType, ColumnType, OptionType } from '../../components/Common/DataTable/DataTypes';
import { faCircleXmark, faTrashCan, faRefresh, faPlay, faPause } from "@fortawesome/free-solid-svg-icons";
import { faAdd } from "@fortawesome/pro-solid-svg-icons";
import { DEFAULT_INPUT_VALIDATION, DEFAULT_RECORD_LIMIT, ID_INPUT_VALIDATION } from "../../components/Common/DataTable/DataTableConsts";
import { BreadCrumbType, PageButtonType } from '../../datatypes/datatypes';
import _ from 'lodash';
import { IUser } from "../../dassTypes";
import { getAppBase } from "../../utils/consts";
import { GenericDassQuery } from "../../services/BasicDassQueries";
import { toast } from "react-toastify";
import { actionIcon } from '../../utils/filters';
import { CampaignStage, CampaignStatus } from "./FuotaTypes";
import { generateDateTimeColumn } from "../../components/Common/columnDefination";
import { dialog } from "../../components/Common";

interface IMyCampaignsStates {
    loggedUser: IUser | null;
    editCampaignId: string;
    addCampaign: boolean;
    showAlertModal: boolean;
    pageTitle: string;
    breadCrumbArr: BreadCrumbType[];
    campaignid: string;
    selectedAction: string;
    showLoadingDialog: boolean;
    showConfirmDialog: boolean;
    confirmDialogeSettings: {
        title: string;
        description: string;
        actionLabel: string;
        confirmAction: () => void;
    },
    refresh: boolean;
    exportInit: number;

};

const MyCampaigns: React.FC<{}> = () => {
    const navigate = useNavigate();
    let { campaignid, tabname } = useParams();

    const [showAbortModal, setShowAbortModal] = useState(false);
    const [showDeleteModal,  setShowDeleteModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    
    const stateInit = {
        loggedUser: null,
        editCampaignId: "",
        addCampaign: false,
        pageTitle: strings.MY_APPLICATIONS_TITLE,     // should be translated
        showAlertModal: false,
        breadCrumbArr: [{label: strings.NAV_DEVICES_ITEM, url:`${getAppBase()}/my-devices`},{label: strings.NAV_FUOTA_CAMPAIGNS_ITEM, url:''}],
        campaignid: '',
        selectedAction: '',
        showLoadingDialog: false,
        showConfirmDialog: false,
        confirmDialogeSettings: {
            title: '',
            description: '',
            actionLabel: '',
            confirmAction: () => { }
        },
        refresh: false,
        exportInit: 0,
    };

    const startSelectedCampaign = async ( row ) => {

        try {
            await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}/action/start`, {
                method: "POST",
            });
            toast.success("Campaign started successfully");
        } catch (e) {
            toast.error(e.message);
        }
        refreshTable();
    }

    const campaignSession = async (row) => {
        let prevPageUrl = `${getAppBase()}/my-campaigns`;
        navigate(`${getAppBase()}/my-campaigns/${row.campaignId}`, {state: {row: row, campaignId:row.campaignId, prevPageUrl}, replace: false})
    }

    const pauseSelectedCampaign = async ( row ) => {
        try {
            await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}/action/pause`, {
                method: "POST",
            });
            toast.success("Campaign paused successfully");
        } catch (e) {
            toast.error(e.message);
        }
        refreshTable();
    }


    const deleteSelectedCampaign = async() => {
        try {
            await GenericDassQuery(`/rest/fuota/campaigns/${selectedCampaign.campaignId}`, {
                method: "DELETE",
            }).then((success:any) => {
                if(success.status == 200) {
                    toast.success(`Campaign deleted successfully`);
                } else if (success.status == 202) {
                    toast.success(`Campaign has been marked as cancelled`);
                }
            }).catch((e) => {
                toast.error(e.message);
            })
        } catch (e) {
            toast.error(e.message);
        }
        refreshTable();
        handleClose();
    }

    const abortSelectedCampaign = async (row) => {
        try {
            await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}/action/abort`, {
                method: "POST",
            });
            toast.success("Campaign aborted successfully");
        } catch (e) {
            toast.error(e.message);
        }
        refreshTable();
    };

    const handleClose = () => {
        setShowAbortModal(false);
        setShowDeleteModal(false);
        setSelectedCampaign(null);
    }

    const abortCampaign = (row) => {
        setShowAbortModal(true);
        setSelectedCampaign(row);
    };

    const deleteCampaign = async (row) => {
        setShowDeleteModal(true);
        setSelectedCampaign(row);
    }

    const [deviceState, setDeviceState] = useState<IMyCampaignsStates>(stateInit);


    const refreshTable = () => {

        setDeviceState(prevState => {
            return { ...prevState, refresh: !prevState.refresh }
        })
    }

    const canStartCampaign = (row) => row.status === CampaignStatus.NotStarted || row.status === CampaignStatus.Paused;
    const canPauseOrAbortCampaign = (row) => row.status === CampaignStatus.InProgress;
    const canDeleteCampaign = (row) =>
        row.status === CampaignStatus.NotStarted ||
        row.status === CampaignStatus.Completed ||
        row.status === CampaignStatus.CompletedWithErrors ||
        row.status === CampaignStatus.ERROR;

    const startBulkCampaigns = async (selectedRows) => {
        const startableRows = selectedRows.filter(canStartCampaign);
        if (startableRows.length === 0) {
            toast.info(strings.MY_CAMPAIGNS_ACTION_START_CONFIRM_MESSAGE);
            return;
        }
        if (startableRows.length > 0) {
            const campaignIds = startableRows.map(row => row?.campaignName ?? row.campaignId);
            const confirmDialogeSettings = {
                title: strings.MY_CAMPAIGNS_ACTION_START_CONFIRM_MESSAGE,
                description: campaignIds.join(", "),
                actionLabel: 'Start',
                faIcon: faPlay
            };
            if (await dialog(confirmDialogeSettings) === true) {
            try {
                await Promise.all(startableRows.map(async row => {
                        try {
                            const resp = await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}/action/start`, {
                                method: "POST",
                            });
                            const isSuccess = resp?.status >= 200 && resp?.status < 300;
                            if (!isSuccess) {
                                toast.error(`Failed to start campaign: ${row?.campaignName || row?.campaignId}`);
                                return false;
                            }
                            return true;
                        } catch (e) {
                            toast.error(`Failed to start campaign: ${row?.campaignName || row?.campaignId}`);
                            return false;
                        }
                    }));
                                        

                toast.success("Campaigns started successfully");
            } catch (e) {
                toast.error(e.message);
            }
            refreshTable();
            }
        }
    };

    const pauseBulkCampaigns = async (selectedRows) => {
        const pausableRows = selectedRows.filter(canPauseOrAbortCampaign);
        if (pausableRows.length === 0) {
            toast.info(strings.MY_CAMPAIGNS_ACTION_PAUSE_CONFIRM_MESSAGE);
            return;
        }
        if (pausableRows.length > 0) {
            const campaignIds = pausableRows.map(row => row?.campaignName ?? row.campaignId);
            const confirmDialogeSettings = {
                title: strings.MY_CAMPAIGNS_ACTION_PAUSE_CONFIRM_MESSAGE,
                description: campaignIds.join(", "),
                actionLabel: 'Pause',
                faIcon: faPause
            };
            if (await dialog(confirmDialogeSettings) === true) {
            try {
                await Promise.all(pausableRows.map(async row => {
                        try {
                            const resp = await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}/action/pause`, {
                                method: "POST",
                            });
                            const isSuccess = resp?.status >= 200 && resp?.status < 300;
                            if (!isSuccess) {
                                toast.error(`Failed to pause campaign: ${row?.campaignName || row?.campaignId}`);
                                return false;
                            }
                            return true;
                        } catch (e) {
                            toast.error(`Failed to pause campaign: ${row?.campaignName || row?.campaignId}`);
                            return false;
                        }
                    }));
                                        

                toast.success("Campaigns paused successfully");
            } catch (e) {
                toast.error(e.message);
            }
            refreshTable();
            }
        }
    };

    const deleteBulkCampaigns = async (selectedRows) => {
        const deletableRows = selectedRows.filter(canDeleteCampaign);
        if (deletableRows.length === 0) {
            toast.info(strings.MY_CAMPAIGNS_ACTION_DELETE_CONFIRM_MESSAGE);
            return;
        }
        if (deletableRows.length > 0) {
            const campaignIds = deletableRows.map(row => row?.campaignName ?? row.campaignId);
            const confirmDialogeSettings = {
                title: strings.MY_CAMPAIGNS_ACTION_DELETE_CONFIRM_MESSAGE,
                description: campaignIds.join(", "),
                actionLabel: 'Delete',
                faIcon: faTrashCan
            };
            if (await dialog(confirmDialogeSettings) === true) {
                try {
                    await Promise.all(deletableRows.map(async row => {
                        try {
                            const resp = await GenericDassQuery(`/rest/fuota/campaigns/${row.campaignId}`, {
                                method: "DELETE",
                            });
                            const isSuccess = resp?.status >= 200 && resp?.status < 300;
                            if (!isSuccess) {
                                toast.error(`Failed to delete campaign: ${row?.campaignName || row?.campaignId}`);
                                return false;
                            }
                            return true;
                        } catch (e) {
                            toast.error(`Failed to delete campaign: ${row?.campaignName || row?.campaignId}`);
                            return false;
                        }
                    }));
                    
                    toast.success("Campaigns deleted successfully");
                } catch (e) {
                    toast.error(e.message);
                }
                refreshTable();
            }
        }
    };

    const getActions = useCallback(() => {
        let actions: ActionType[] = [
            {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_START,
                render: (row, title) => actionIcon(row.campaignName, title, faPlay.iconName),
                visible: (row) => canStartCampaign(row),
                action: (row) => startSelectedCampaign(row),
            },
            {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_PAUSE,
                render: (row, title) => actionIcon(row.campaignName, title, faPause.iconName),
                visible: (row) => canPauseOrAbortCampaign(row),
                action: (row) => pauseSelectedCampaign(row),
            }, {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_DELETE,
                render: (row, title) => actionIcon(row.campaignName, title, faTrashCan.iconName),
                visible: (row) => canDeleteCampaign(row),
                action: (row) => deleteCampaign(row),
            }, {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_ABORT,
                render: (row, title) => actionIcon(row.campaignName, title, faCircleXmark.iconName),
                visible: (row) => canPauseOrAbortCampaign(row),
                action: (row) => abortCampaign(row),
            }
        ];

        let bulkActions: BulkActionType[] = [
            {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_START,
                icon: faPlay,
                action: startBulkCampaigns,
                //visible: (selectedRows) => selectedRows?.some(canStartCampaign)
                selectAllBulkActionWarningMessage: strings.MY_CAMPAIGNS_ACTION_START_CONFIRM_MESSAGE
            },
            {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_PAUSE,
                icon: faPause,
                action: pauseBulkCampaigns,
                // visible: (selectedRows) => selectedRows?.some(canPauseOrAbortCampaign)
                selectAllBulkActionWarningMessage: strings.MY_CAMPAIGNS_ACTION_PAUSE_CONFIRM_MESSAGE
            },
            {
                type: "action",
                text: strings.MY_CAMPAIGNS_ACTION_DELETE,
                icon: faTrashCan,
                action: deleteBulkCampaigns,
                selectAllBulkActionWarningMessage: strings.MY_CAMPAIGNS_ACTION_DELETE_CONFIRM_MESSAGE,
                // visible: (selectedRows) => selectedRows?.length > 0
            }
        ];

        return { actions, bulkActions }
    }, []);

    const getPageButtons = useCallback(() => {

        const pageButtons: PageButtonType[] = [
            {
                title: strings.REFRESH_LIST,
                action: () => { refreshTable() },
                type: 'button',
                icon: faRefresh
            },
            {
                title: 'separator',
                type: 'separator',
            },
            {
                title: strings.ADD_FUOTA_CAMPAIGN_BTN,
                action: () => {
                    navigate(`${getAppBase()}/my-campaigns/create`);
                },
                type: 'button_with_icon',
                icon: faAdd
            },
        ]

        return pageButtons;
    }, []);

    const initDataTable = () => {

        let actions: any = [];
        let pageButtons: PageButtonType[] = [];

        actions = getActions();
        // const renderDeviceIds = (row) => {
        //     const list = row.devices.map(device => {
        //         return <div>{device}</div>
        //     })
        //     return list;
        // }

        let columns: ColumnType[] = [
             {
                key: "bulk_action_checkbox",
                type: "bulk_action_checkbox",
                title: "Bulk Action",
                filterable: false,
                cellWidth: 3,
                customClass: "left",
            },
            {
                key: "campaignName",
                type: "text",
                title: strings.CAMPAIGN_NAME,
                maxInputLength: 40,
                filterable: true,
                filterField: "search_name",
                filterType: "text",
                inputValidation: ID_INPUT_VALIDATION,
                sortable: true,
                sortKey: "sort_by_name",
                cellWidth: 20,
                detailLink: true,
                copyLink: false,
                detailPageNav: (row) =>  campaignSession(row),
                width: 120,
                minWidth: 120,
                flex: 2,
                resizable: true,
                customClass: "left",
                showCopyLink: true
            },
            {
                key: "description",
                type: "text",
                title: strings.CAMPAIGN_DESCRIPTION,
                maxInputLength: 40,
                filterable: true,
                filterField: "search_description",
                filterType: "text",
                inputValidation: ID_INPUT_VALIDATION,
                sortable: true,
                sortKey: "sort_by_description",
                cellWidth: 20,
                copyLink: false,
                width: 100,
                minWidth: 100,
                flex: 2,
                resizable: true,
                showCopyLink: true

            },
            {
                key: "firmwareName",
                title: strings.CAMPAIGN_FIRMWARE,
                type: "text",
                filterable: true,
                filterField: "search_firmware",
                filterType: "text",
                inputValidation: DEFAULT_INPUT_VALIDATION,
                sortable: true,
                sortKey: "sort_by_firmware",
                filterParams: {},
                cellWidth: 10,
                width: 200,
                minWidth: 120,
                flex: 2,
                resizable: true,
                showCopyLink: true,
            },
            {
                key: "devices",
                title: strings.CAMPAIGN_DEVICES,
                type: "text",
                filterable: true,
                filterField: "search_num_devices",
                filterType: "text",
                dataAlign: 'center',
                render: (row) => { return row?.devices?.length || 0 },
                inputValidation: DEFAULT_INPUT_VALIDATION,
                sortable: true,
                sortKey: "sort_by_num_devices",
                filterParams: {},
                cellWidth: 5,
                width: 90,
                minWidth: 90,
                maxWidth: 90,
            },
            {
                key: "sessions",
                title: strings.CAMPAIGN_GROUPS,
                type: "text",
                render: (row) => {return row?.sessions?.length || 0},
                filterable: false,
                filterField: "search_comment",
                filterType: "text",
                dataAlign: 'center',
                inputValidation: DEFAULT_INPUT_VALIDATION,
                sortable: false,
                sortKey: "sort_by_comment",
                filterParams: {},
                cellWidth: 'nowrap',
                width: 70,
                minWidth: 70,
                maxWidth: 70,
                
            },
            {
                key: "status",
                title: strings.CAMPAIGN_STATUS,
                type: "text",
                filterable: true,
                filterField: "search_status",
                filterType: "select",
                filterParams: {
                    options: Object.entries(CampaignStatus).map(([_, value]) => ({
                        label: value,
                        value,
                    }))
                },
                render: (row) => {
                    const status = row.status as CampaignStatus;
                    return status ?? "";
                },
                sortable: false,
                sortKey: "sort_by_status",
                width: 100,
                minWidth: 100,
                maxWidth: 300,
                resizable: true,

            },
            {
                key: "stage",
                title: strings.CAMPAIGN_STAGE,
                type: "text",
                filterable: true,
                filterField: "search_stage",
                filterType: "select",
                filterParams: {
                    options: Object.entries(CampaignStage).map(([_, value]) => ({
                        label: value,
                        value,
                    }))
                },
                render: (row) => {
                    const stage = row.stage as CampaignStage;
                    return stage ?? "";
                },
                sortable: true,
                sortKey: "sort_by_stage",
                width: 100,
                minWidth: 100,
                resizable: true,
                flex: 2,
            },
            generateDateTimeColumn({
                key: 'createdAt',
                title: strings.CAMPAIGN_CREATION_TIME,
                filterable: true,
                filterField: "date",
                filterParams: {
                    startField: "from_creation_date",
                    endField: "to_creation_date",
                    mapper: (x) => x && x.format()
                },
                sortable: true,
                sortKey: "sort_by_creation_date",
            }),
            generateDateTimeColumn({
                key: 'startDate',
                title: strings.CAMPAIGN_UNICAST_SETUP_DEADLINE,
                filterable: true,
                filterField: "date",
                filterParams: {
                    startField: "from_start_date",
                    endField: "to_start_date",
                    mapper: (x) => x && x.format()  
                },
                sortable: true,
                sortKey: "sort_by_start_date"
            }),
            generateDateTimeColumn({
                key: 'endDate',
                title: strings.CAMPAIGN_END_TIME,
                filterable: true,
                filterField: "date",
                filterParams: {
                    startField: "from_end_date",
                    endField: "to_end_date",
                    mapper: (x) => x && x.format()
                },
                sortable: true,
                sortKey: "sort_by_end_date"
            }),
            {
                key: 'action_button',
                type: "action_button",
                title: 'Actions',
                filterable: false,
                cellWidth: 3,
                newCellWidth: '120px',
                customClass: 'right',
            },
        ];

        let options: OptionType = {
            url: '/uiapi/rest/fuota/campaigns',
            query_param: { all: true, get_pages: true, limit: DEFAULT_RECORD_LIMIT, stream: 'progress' },
            serial_number: false,
            id_field: 'campaignId',
            oboe_path: 'pages.*',
            available_key: 'campaignId',
            allowBulkActions: true,
            defaultSortField: 'sort_by_creation_date',
            defaultSortOrder: 'desc',
            emptyDataMsg: '<b>Sorry!</b> No FUOTA Campaign Available',
            emptyDataButton: {
                title: strings.FUOTA_BTN,
                action: () => { navigate(`${getAppBase()}/my-campaigns/create`)},
                type: 'button_with_icon',
                icon: faAdd
            },
            agGridAdjustWidthKeys: ['description']
        }

        return {
            ...actions,
            columns: columns,
            ...options,
            pageButons: pageButtons
        };
    }

    let refresh = deviceState.refresh;

    return (
        <>
            <PageContent
                name="my-campaigns"
                id={campaignid}
                tabname={tabname}
                actions={getActions()}
                breadCrumbArr={deviceState.breadCrumbArr}
                pageButtons={getPageButtons()}
                countLabel={`FUOTA Campaigns`}
                dataTableOption={initDataTable()}
                exportInit={deviceState.exportInit}
                refresh={refresh}
            />
            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header className="border-bottom-0" closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Fuota Campaign
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    Are you sure you want to delete this FUOTA campaign?
                </Modal.Body>
                <Modal.Footer className="border-top-0">
                    <Button variant="outline-dark" size="sm" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="dark" size="sm" onClick={deleteSelectedCampaign}>
                        Ok
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Abort Confirmation Modal */}
            <Modal
                show={showAbortModal}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header className="border-bottom-0" closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Fuota Campaign
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    Are you sure you want to <strong>abort</strong> this FUOTA campaign?
                </Modal.Body>
                <Modal.Footer className="border-top-0">
                    <Button variant="outline-dark" size="sm" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                            abortSelectedCampaign(selectedCampaign);
                            handleClose();
                        }}
                    >
                        Abort
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default MyCampaigns;